import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const RECOS_OPEN_KEY = 'zafra:cart-recos-open';
const TOTAL_OPEN_KEY = 'zafra:cart-total-open';

/**
 * Citeste starea memorata a unui acordeon din sertar.
 *
 * Sertarul se re-randeaza la fiecare modificare de cos si markup-ul de pe server
 * vine mereu inchis, asa ca starea aleasa de client traieste in sessionStorage.
 * Accesul poate arunca (private mode, cookies blocate) — de aici try/catch.
 *
 * @param {string} key
 * @returns {boolean}
 */
function readOpenState(key) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/**
 * @param {string} key
 * @param {boolean} open
 */
function writeOpenState(key, open) {
  try {
    sessionStorage.setItem(key, open ? '1' : '0');
  } catch {
    // Stocare blocata — acordeonul ramane pe starea din DOM.
  }
}

/**
 * Adaugarea rapida din acordeonul de recomandari al sertarului de cos.
 *
 * Merge pe acelasi drum ca `product-form-component`: un `CartLinesUpdateEvent`
 * cu promisiune amanata, rezolvat cu sectiunile randate de `/cart/add`. Asa
 * bulina din header, lista de produse, bara de transport si totalul se
 * actualizeaza toate din acelasi eveniment, fara sa atingem intern morph-ul.
 */
class ZafraCartRecos extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#onClick, { signal });

    const details = this.#details;
    if (details) {
      details.open = this.#storedOpenState;
      details.addEventListener('toggle', this.#onToggle, { signal });
    }

    // Sectiunea se re-randeaza la fiecare modificare de cos, iar markup-ul de pe
    // server vine mereu inchis — reaplicam starea aleasa de client dupa morph.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#restoreOpenState, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /** @returns {HTMLDetailsElement | null} */
  get #details() {
    return this.querySelector('details');
  }

  /** @returns {boolean} */
  get #storedOpenState() {
    return readOpenState(RECOS_OPEN_KEY);
  }

  #onToggle = () => {
    writeOpenState(RECOS_OPEN_KEY, Boolean(this.#details?.open));
  };

  #restoreOpenState = () => {
    requestAnimationFrame(() => {
      const details = this.#details;
      if (details) details.open = this.#storedOpenState;
    });
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.av-reco__add');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();
    this.#addToCart(button);
  };

  /** @param {HTMLButtonElement} button */
  #addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;

    const sectionIds = [];
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.push(component.dataset.sectionId);
      }
    }

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
      }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          button.disabled = false;
          const message = response.message || this.dataset.errorAdd || '';
          this.dispatchEvent(
            new CartErrorEvent({
              error: message,
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          deferred.reject(new Error(message));
          return;
        }

        button.classList.add('is-added');

        const cart = await fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((res) => res.json());

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'zafra-cart-recos',
            itemCount: 1,
            productId: button.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });
      })
      .catch((error) => {
        button.disabled = false;
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: this.dataset.errorNetwork || error?.message || '',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      });
  }
}

/**
 * Detalierea pretului („Vezi detalii") de deasupra totalului. Singura ei sarcina
 * este sa supravietuiasca morph-ului sectiunii.
 */
class ZafraCartTotal extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;
    const details = this.#details;
    if (!details) return;

    details.open = readOpenState(TOTAL_OPEN_KEY);
    details.addEventListener('toggle', this.#onToggle, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#restoreOpenState, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /** @returns {HTMLDetailsElement | null} */
  get #details() {
    return this.querySelector('details');
  }

  #onToggle = () => {
    writeOpenState(TOTAL_OPEN_KEY, Boolean(this.#details?.open));
  };

  #restoreOpenState = () => {
    requestAnimationFrame(() => {
      const details = this.#details;
      if (details) details.open = readOpenState(TOTAL_OPEN_KEY);
    });
  };
}

if (!customElements.get('zafra-cart-recos')) {
  customElements.define('zafra-cart-recos', ZafraCartRecos);
}

if (!customElements.get('zafra-cart-total')) {
  customElements.define('zafra-cart-total', ZafraCartTotal);
}
